import { useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  User as UserIcon,
  Settings as SettingsIcon,
  Building2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AppSwitcher,
  toWorkspaceApps,
  PlatformTopbar,
  TenantSwitcher,
  UserMenu,
  UserMenuItem,
} from '@zafroi/ui-kit';
import type { TenantOption, UserMenuIdentity } from '@zafroi/ui-kit';
import { MobileNav } from './MobileNav';
import { authService } from '@/services/auth';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { session } from '@/lib/session';
import { env } from '@/lib/env';
import {
  isOnCanonicalLoginOrigin,
  redirectToPlatformLogin,
} from '@/lib/platform-login';

const ROLE_LABEL: Record<string, string> = {
  admin: 'Admin',
  manager: 'Manager',
  operator: 'Operator',
  viewer: 'Viewer',
};

const TIER_LABEL: Record<string, string> = {
  PLATFORM_ADMIN: 'Platform · ZAFROI Ops',
  TENANT_ADMIN: 'Tenant · Admin',
  INTEGRATION: 'Integration',
  PORTAL: 'Portal',
};

/**
 * Sticky topbar — byte-identical to engage.zafroi.com:
 *
 *   leading  : MobileNav (mobile-only) · AppSwitcher · "/" · TenantSwitcher
 *   trailing : UserMenu
 *
 * Search palette (⌘K), breadcrumb, notifications bell, health/session/
 * tenant indicators all moved out of the topbar to keep both apps
 * visually identical. The ⌘K palette is still wired via keyboard
 * shortcut — we just dropped its visible trigger button. The rest are
 * tracked for relocation under "Platform shell unification" in
 * zafroi-engage-app/docs/BACKLOG.md.
 */
export function Topbar() {
  const me = useCurrentUser();
  return (
    <TooltipProvider delayDuration={150}>
      <PlatformTopbar
        leading={
          <>
            <MobileNav />
            {/* ADR-037: apps come from the runtime registry (/auth/me workspace.apps),
                already permission-filtered server-side.
                Single-workspace tenants (SOFTWARE's unified nav, REAL_ESTATE's single
                app) never see the ecosystem launcher — the server decides, once, in
                `workspace.singleWorkspace` (ADR-038/041). Operations rendered it
                unconditionally, so those verticals saw a switcher here even though
                every other surface hid it. */}
            {!me?.workspace?.singleWorkspace && (
              <>
                <AppSwitcher
                  apps={toWorkspaceApps(me?.workspace?.apps)}
                  currentAppId="operations"
                />
                <span className="hidden text-sm text-muted-foreground md:inline">
                  /
                </span>
              </>
            )}
            <OpsTenantSwitcher />
          </>
        }
        trailing={<OpsUserMenu />}
      />
    </TooltipProvider>
  );
}

// ── Tenant switcher (kit) — fed from the signed-in user ────────────────

function OpsTenantSwitcher() {
  const me = useCurrentUser();
  // PLATFORM_ADMIN doesn't have a single "workspace" — hide the switcher
  // for them (matches the prior TenantPill behaviour).
  if (!me || me.tier === 'PLATFORM_ADMIN') return null;
  const tenant: TenantOption = {
    id: me.tenantId,
    name: me.tenantName,
    code: me.tenantCode,
  };
  return (
    <TenantSwitcher
      tenants={[tenant]}
      currentTenantId={tenant.id}
      onTenantChange={() => {
        /* single-tenant for now — multi-tenant memberships are a
           follow-up tracked in engage-app/docs/BACKLOG.md. */
      }}
    />
  );
}

// ── User menu (kit) — preserves Ops's menu items + sign-out ────────────

function OpsUserMenu() {
  const me = useCurrentUser();
  const navigate = useNavigate();
  const usingApiKeyFallback = !me && !!env.apiKey;

  const onSignOut = async () => {
    try {
      await authService.logout();
    } catch {
      /* swallow */
    }
    session.clear();
    toast.success('Signed out');
    if (!isOnCanonicalLoginOrigin()) {
      redirectToPlatformLogin();
      return;
    }
    navigate('/login', { replace: true });
  };

  if (usingApiKeyFallback) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="hidden items-center gap-1.5 rounded-md border border-warning/30 bg-warning/10 px-2.5 py-1 text-[11px] font-medium [color:hsl(38_85%_25%)] sm:inline-flex">
            <AlertCircle className="h-3 w-3" />
            Dev · API key mode
          </span>
        </TooltipTrigger>
        <TooltipContent>No human session — using VITE_API_KEY for auth</TooltipContent>
      </Tooltip>
    );
  }
  if (!me) return null;

  const isPlatformAdmin = me.tier === 'PLATFORM_ADMIN';
  const identity: UserMenuIdentity = {
    name: me.fullName,
    email: me.email,
    // Show the user's REAL roles (Owner/Admin/Business Head/…); fall back to the
    // legacy enum label only for older sessions that predate `roles` on /auth/me.
    subtitle: `${TIER_LABEL[me.tier] ?? me.tier} · ${
      me.roles?.length ? me.roles.join(', ') : (ROLE_LABEL[me.role] ?? me.role)
    }`,
  };

  return (
    <UserMenu user={identity} onSignOut={onSignOut}>
      <UserMenuItem onSelect={() => navigate('/settings/profile')}>
        <UserIcon className="h-4 w-4" />
        Profile &amp; security
      </UserMenuItem>
      <UserMenuItem onSelect={() => navigate('/settings/integrations/health')}>
        <Activity className="h-4 w-4" />
        Integration health
      </UserMenuItem>
      {!isPlatformAdmin && (
        <UserMenuItem onSelect={() => navigate('/platform/users')}>
          <Building2 className="h-4 w-4" />
          Workspace settings
        </UserMenuItem>
      )}
      {isPlatformAdmin && (
        <UserMenuItem onSelect={() => navigate('/platform/settings')}>
          <SettingsIcon className="h-4 w-4" />
          Platform settings
        </UserMenuItem>
      )}
    </UserMenu>
  );
}

